/**
 * MyWorkflowTab — the "My Workflow" tab on My Page.
 *
 * Four sections:
 *   1. Review & Approve  -> WAS   (items pending the user's approval)
 *   2. Upload            -> FRISCO
 *   3. Execute           -> FLEXICO
 *   4. Categorize        -> LOGICO
 *
 * Review & Approve is sourced from WAS (getApproverWasWorkflows, already
 * cross-app) and shows ID, Title, Status, Submitted Date. The other three come
 * from a single cross-app query (getMyWorkflowItems) that returns domain-tagged
 * rows listing the processes the user can upload/execute/categorize; this
 * component buckets them by `domain` and shows the per-domain ID
 * (Frisco/Flexico/Logico ID), Title, Application, and the Authorized Users. Each
 * process container has a free-text filter over ID, Application, Title, and
 * Authorized Users; Review & Approve has a filter over Title.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";

// Rows shown per page in each workflow section.
const PAGE_SIZE = 5;

import "./homepage.scss";
import { getMyWorkflowItems } from "src/utilities/AppSyncPlusAPIs";
import { getApproverWasWorkflows } from "src/utilities/wasApprovalAPIs";
import { fetchAndCacheAllLdaps } from "src/checkPageAccess";
import { convertEpochToPST } from "src/Ateam-BI/Frisco/FilterUtilities";

// Per-domain destination for a workflow item: the action page where the user
// performs Upload/Execute/Categorize for that process.
//   FRISCO   -> /App/:appId/process/:id/upload
//   FLEXICO  -> /App/:appId/flexico/:id/upload
//   LOGICO   -> /App/:appId/LogicoProcess/:id/upload
const processHref = (domain, appId, id) => {
  if (appId == null || id == null) return null;
  switch (domain) {
    case "FRISCO":
      return `/App/${appId}/process/${id}/upload`;
    case "FLEXICO":
      return `/App/${appId}/flexico/${id}/upload`;
    case "LOGICO":
      return `/App/${appId}/LogicoProcess/${id}/upload`;
    default:
      return null;
  }
};

// WAS approval item destination.
const wasHref = (workflowInstanceId) =>
  workflowInstanceId ? `/ApprovalsHome/Workflow/${workflowInstanceId}` : null;

// Section metadata: label, one-line description, and (for the process-backed
// sections) which `domain` of the getMyWorkflowItems response feeds it.
const PROCESS_SECTIONS = [
  { key: "UPLOAD", domain: "FRISCO", label: "Upload", description: "FRISCOs you can upload to" },
  { key: "EXECUTE", domain: "FLEXICO", label: "Execute", description: "FLEXICOs you can execute" },
  { key: "CATEGORIZE", domain: "LOGICO", label: "Categorize", description: "LOGICOs you can categorize" },
];

// Per-domain ID column header.
const DOMAIN_ID_HEADER = {
  FRISCO: "Frisco ID",
  FLEXICO: "Flexico ID",
  LOGICO: "Logico ID",
};

// The searchable text of a process row (ID, Application, Title, Uploaders) for
// the per-container filter. Matched case-insensitively as a substring.
const processMatchText = (item) => {
  const uploaderAliases = Array.isArray(item.uploaders)
    ? item.uploaders.map((u) => u?.alias).filter(Boolean)
    : [];
  return [item.id, item.app_name, item.title, ...uploaderAliases]
    .map((v) => (v == null ? "" : String(v)))
    .join(" ")
    .toLowerCase();
};

// Epoch-ms -> PST date string, consistent with the Approvals pages (which show
// WAS createdOn via convertEpochToPST). Tolerant of string/number/blank.
const formatSubmittedDate = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return convertEpochToPST(n);
};

// Configured uploaders -> comma-separated aliases. Each entry is
// { alias, type } (type UPLOADER = individual, UPLOADER_LDAP = group).
const formatUploaders = (uploaders) => {
  if (!Array.isArray(uploaders) || uploaders.length === 0) return "—";
  const aliases = uploaders
    .map((u) => u?.alias)
    .filter(Boolean);
  return aliases.length ? aliases.join(", ") : "—";
};

// WAS instance title lives in a `source_title` tag, else the workflow name.
const wasTitle = (wf) => {
  const tag = (wf?.tags || []).find((t) => t?.key === "source_title");
  return tag?.value || wf?.name || wf?.workflowInstanceId || "—";
};

// Searchable text for a WAS (Review & Approve) row — its title.
const wasMatchText = (wf) => wasTitle(wf).toLowerCase();

// A clickable ID cell that navigates within the SPA (falls back to plain text
// when there's no resolvable destination).
const IdLink = ({ text, href, onNavigate }) => {
  if (!href) return <span>{text}</span>;
  return (
    <Link
      href={href}
      onFollow={(e) => {
        e.preventDefault();
        onNavigate(href);
      }}
    >
      {text}
    </Link>
  );
};

const buildProcessColumns = (onNavigate, idHeader = "ID") => [
  {
    id: "id",
    header: idHeader,
    cell: (item) => (
      <IdLink
        text={String(item.id ?? "—")}
        href={processHref(item.domain, item.app_id, item.id)}
        onNavigate={onNavigate}
      />
    ),
    width: 140,
  },
  { id: "title", header: "Title", cell: (item) => item.title || "—", minWidth: 240 },
  { id: "application", header: "Application", cell: (item) => item.app_name || "—", minWidth: 200 },
  { id: "uploaders", header: "Authorized Users", cell: (item) => formatUploaders(item.uploaders), minWidth: 200 },
];

const buildWasColumns = (onNavigate) => [
  {
    id: "id",
    header: "ID",
    cell: (item) => (
      <IdLink
        text={item.workflowInstanceId || "—"}
        href={wasHref(item.workflowInstanceId)}
        onNavigate={onNavigate}
      />
    ),
    minWidth: 200,
  },
  { id: "title", header: "Title", cell: (item) => wasTitle(item), minWidth: 240 },
  {
    id: "status",
    header: "Status",
    cell: () => <StatusIndicator type="pending">Pending Approval</StatusIndicator>,
    width: 200,
  },
  { id: "submitted", header: "Submitted Date", cell: (item) => formatSubmittedDate(item.createdOn), width: 160 },
];

function WorkflowSection({
  label,
  description,
  count,
  loading,
  columnDefinitions,
  items,
  emptyText,
  filterable = false,
  matchText = processMatchText,
  filterPlaceholder = "Search by ID, Application, Title, or Authorized Users",
}) {
  // Independent client-side pagination for this section (5 rows/page).
  const [pageIndex, setPageIndex] = useState(1);
  // Free-text filter for this container. What it matches is supplied by
  // `matchText` (process sections search ID/Application/Title/Authorized Users;
  // Review & Approve searches title). Case-insensitive.
  const [filterText, setFilterText] = useState("");

  const filteredItems = useMemo(() => {
    if (!filterable) return items;
    const q = filterText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => matchText(item).includes(q));
  }, [filterable, filterText, items, matchText]);

  const pagesCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));

  // Keep the current page valid if the list shrinks (e.g. after a reload or as
  // the filter narrows results).
  useEffect(() => {
    if (pageIndex > pagesCount) setPageIndex(pagesCount);
  }, [pageIndex, pagesCount]);

  const pageItems = useMemo(
    () => filteredItems.slice((pageIndex - 1) * PAGE_SIZE, pageIndex * PAGE_SIZE),
    [filteredItems, pageIndex]
  );

  return (
    <Container
      header={
        <Header variant="h2" counter={!loading ? `(${count})` : undefined} description={description}>
          {label}
        </Header>
      }
    >
      <Table
        variant="embedded"
        columnDefinitions={columnDefinitions}
        items={pageItems}
        loading={loading}
        loadingText="Loading items"
        sortingDisabled
        filter={
          filterable ? (
            <TextFilter
              filteringText={filterText}
              filteringPlaceholder={filterPlaceholder}
              filteringAriaLabel="Search items"
              countText={
                filterText.trim()
                  ? `${filteredItems.length} ${filteredItems.length === 1 ? "match" : "matches"}`
                  : undefined
              }
              onChange={({ detail }) => {
                setFilterText(detail.filteringText);
                setPageIndex(1);
              }}
            />
          ) : undefined
        }
        empty={
          <Box textAlign="center" color="inherit" padding={{ vertical: "l" }}>
            <b>{filterable && filterText.trim() ? "No matches" : emptyText}</b>
          </Box>
        }
        pagination={
          pagesCount > 1 ? (
            <Pagination
              currentPageIndex={pageIndex}
              pagesCount={pagesCount}
              onChange={({ detail }) => setPageIndex(detail.currentPageIndex)}
            />
          ) : undefined
        }
      />
    </Container>
  );
}

function MyWorkflowTab() {
  const history = useHistory();
  const userId = useSelector((globalState) => globalState.auth.userId);
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);

  const navigate = useMemo(() => (href) => history.push(href), [history]);
  const wasColumnDefinitions = useMemo(() => buildWasColumns(navigate), [navigate]);

  const [wasItems, setWasItems] = useState([]);
  const [wasLoading, setWasLoading] = useState(true);
  const [processItems, setProcessItems] = useState([]); // domain-tagged rows
  const [processLoading, setProcessLoading] = useState(true);
  // The user's full LDAP group membership, sourced from BRASS (cached at
  // `brass_cache:all_ldaps:<stage>`), NOT the Cognito token claim — the token's
  // `custom:LDAP_GROUPS` can be truncated for users in many groups. `null`
  // until resolved, so the process query waits for the complete list.
  const [groups, setGroups] = useState(null);

  // Fetch the complete LDAP membership from BRASS (via the shared cache) once
  // the stage is known. fetchAndCacheAllLdaps handles cache hit/miss/expiry and
  // the dev->beta API mapping; it returns the list (array or JSON string).
  useEffect(() => {
    let cancelled = false;
    if (!currentStage) {
      // No stage yet -> resolve to an empty membership so the process query can
      // still proceed (matching only the user's individual alias) instead of
      // leaving `groups` null and spinning the process sections forever.
      setGroups([]);
      return undefined;
    }
    fetchAndCacheAllLdaps(currentStage)
      .then((result) => {
        if (cancelled) return;
        let list = result;
        if (typeof list === "string") {
          try {
            list = JSON.parse(list);
          } catch (e) {
            list = [];
          }
        }
        setGroups(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentStage]);

  // Review & Approve (WAS) — items still pending the user's approval.
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setWasItems([]);
      setWasLoading(false);
      return undefined;
    }
    if (!currentStage) {
      // Stage not resolved yet — getApproverWasWorkflows builds its query
      // directly from `stage`, so firing now would send a literal
      // `stage: "undefined"` request that refires (and flickers) once the stage
      // arrives. Stay in the loading state and wait, consistent with the groups
      // fetch above.
      setWasLoading(true);
      return undefined;
    }
    setWasLoading(true);

    // getApproverWasWorkflows is server-side paginated: without pageSize/pageNo
    // it returns only the backend's default page, while metaData carries the
    // true totals. Page through until we've collected every pending item so the
    // client-side pagination and the section counter reflect the full set
    // (rather than silently dropping items beyond the first page).
    //
    // pageNo is 0-indexed (first page = 0), matching Approvals Home; metaData
    // exposes totalPages. A hard page cap guards against a misbehaving backend
    // so we can't loop forever.
    const fetchAllWasItems = async () => {
      const pageSize = 50;
      const MAX_PAGES = 100; // safety cap (up to 5000 items)
      let pageNo = 0;
      let totalPages = 1;
      let all = [];
      while (pageNo < totalPages && pageNo < MAX_PAGES) {
        // eslint-disable-next-line no-await-in-loop
        const res = await getApproverWasWorkflows({
          userAliasAllStates: userId,
          stage: currentStage,
          userResponseStatus: "PENDING",
          // Constrain to workflows that are themselves still open — otherwise a
          // completed/rejected workflow where this user's response is still
          // PENDING would leak in.
          workflowStatus: "pending",
          pageSize,
          pageNo,
        });
        const batch = Array.isArray(res?.workflowInstances) ? res.workflowInstances : [];
        all = all.concat(batch);
        const reportedPages = Number(res?.metaData?.totalPages);
        if (Number.isFinite(reportedPages) && reportedPages > 0) {
          totalPages = reportedPages;
        }
        // Stop early if a page comes back short/empty (last page or no metadata).
        if (batch.length < pageSize) break;
        pageNo += 1;
      }
      return all;
    };

    fetchAllWasItems()
      .then((items) => {
        if (cancelled) return;
        setWasItems(items);
      })
      .catch(() => {
        if (!cancelled) setWasItems([]);
      })
      .finally(() => {
        if (!cancelled) setWasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, currentStage]);

  // Upload / Execute / Categorize (FRISCO / FLEXICO / LOGICO) — one cross-app
  // call. Waits until the BRASS group list has resolved (`groups !== null`) so
  // the UPLOADER_LDAP match runs against the complete membership.
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setProcessItems([]);
      setProcessLoading(false);
      return undefined;
    }
    if (groups === null) {
      // Groups still loading from BRASS — keep the loading state and wait.
      setProcessLoading(true);
      return undefined;
    }
    setProcessLoading(true);
    getMyWorkflowItems(userId, groups)
      .then((rows) => {
        if (cancelled) return;
        setProcessItems(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setProcessItems([]);
      })
      .finally(() => {
        if (!cancelled) setProcessLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, groups]);

  const itemsByDomain = useMemo(() => {
    const map = { FRISCO: [], FLEXICO: [], LOGICO: [] };
    processItems.forEach((row) => {
      if (map[row.domain]) map[row.domain].push(row);
    });
    return map;
  }, [processItems]);

  return (
    <SpaceBetween size="l">
      <Container>
        <Box variant="p" color="text-body-secondary">
          The processes you can act on across Accounting Central, organized by
          action type: Review &amp; Approve, Upload, Execute, and Categorize.
        </Box>
      </Container>

      {/* Review & Approve — WAS */}
      <WorkflowSection
        label="Review & Approve"
        description="Items awaiting your review or approval"
        count={wasItems.length}
        loading={wasLoading}
        columnDefinitions={wasColumnDefinitions}
        items={wasItems}
        emptyText="Nothing awaiting your approval"
        filterable
        matchText={wasMatchText}
        filterPlaceholder="Search by Title"
      />

      {/* Upload / Execute / Categorize — FRISCO / FLEXICO / LOGICO */}
      {PROCESS_SECTIONS.map((section) => (
        <WorkflowSection
          key={section.key}
          label={section.label}
          description={section.description}
          count={itemsByDomain[section.domain].length}
          loading={processLoading}
          columnDefinitions={buildProcessColumns(
            navigate,
            DOMAIN_ID_HEADER[section.domain] || "ID"
          )}
          items={itemsByDomain[section.domain]}
          emptyText={`No ${section.label.toLowerCase()} items`}
          filterable
        />
      ))}
    </SpaceBetween>
  );
}

export default MyWorkflowTab;
